/**
 * Shared S3-prefix builders for the project photo batch
 * (`listProjectPhotoBatch` returns `Record<prefix, PhotoItem[]>`).
 *
 * Both the legacy CollectorenTab and the project Dashboard look photos up in the
 * same batch map, so the prefix MUST be constructed identically on both sides —
 * any drift means the lookup silently misses and photos appear absent. Keep these
 * two builders the single source of truth for those prefixes.
 */
import type { Collector, PhotoItem } from '@/types/odoo';
import { getCollectorMeta } from '@/utils/collectors';

/** Collector photos: `{projectName}/Blok-{blok}/{verdieping}/{collectorName}/`. */
export function collectorPhotoKey(collector: Collector, projectName: string): string {
  const { blok, verdieping, collectorName } = getCollectorMeta(collector, projectName);
  return `${projectName}/Blok-${blok}/${verdieping}/${collectorName}/`;
}

/** Drukattest photos (per blok): `{projectName}/drukattest/{blok}/`. */
export function drukattestKey(projectName: string, blok: string): string {
  return `${projectName}/drukattest/${blok}/`;
}

/**
 * All drukattest photos of one blok from the batch. Aannemer uploads live one
 * level deeper (`{blok}/{partnerId}/`) and the batch groups by exact folder, so
 * every group under the blok prefix is merged, newest first. The trailing slash
 * of the prefix keeps "Hoofdgebouw" and "Hoofdgebouw 2" apart. `undefined` while
 * the batch is still loading, like a direct batch lookup.
 */
export function drukattestPhotosFromBatch(
  batch: Record<string, PhotoItem[]> | undefined,
  projectName: string,
  blok: string,
): PhotoItem[] | undefined {
  if (!batch) return undefined;
  const prefix = drukattestKey(projectName, blok);
  return Object.entries(batch)
    .filter(([key]) => key.startsWith(prefix))
    .flatMap(([, photos]) => photos)
    .sort((a, b) => (b.lastModified ?? '').localeCompare(a.lastModified ?? ''));
}
